import { useEffect } from 'react';

import { useRouter } from 'next/router';

import {
  type AttributionInput,
  readAttributionParams,
} from '@onark/shared/attribution';
import { secureCookie } from '@OnArk/utils/cookieSecure';
import Cookies from 'universal-cookie';

// First touch only, by decision (onark-marketing-system#10): the goal metric
// credits first touch; last touch is derived from PostHog, not stored here.
const COOKIE = 'onark-attribution';
// 90 days: long enough to attribute a signup back to the ad/referrer that
// first brought the person to the site, across return visits.
const MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

// The attribution shape is owned by @onark/shared (one definition for api,
// worker, frontend, marketing). This is just its browser-facing alias.
export type FirstTouch = AttributionInput;

function readFromUrl(): FirstTouch {
  // Query-param-derived fields, via the shared parser so a new ad param is
  // added in one place and the malformed-link guard stays common to both
  // capture sites (see docs/attribution-ad-url-convention.md).
  const touch: FirstTouch = readAttributionParams(window.location.search);

  // Derived from the DOM, not the query string.
  touch.landingPath = window.location.pathname || undefined;
  // Only an off-site referrer is meaningful as origin; same-origin nav isn't.
  if (
    document.referrer &&
    !document.referrer.startsWith(window.location.origin)
  )
    touch.referrer = document.referrer;

  return touch;
}

export function getFirstTouch(): FirstTouch {
  try {
    // universal-cookie JSON-parses when it can; a malformed cookie comes back
    // as the raw string, which the typeof check filters out.
    const stored = new Cookies().get<FirstTouch | string | undefined>(COOKIE);

    return stored && typeof stored === 'object' ? stored : {};
  } catch {
    return {};
  }
}

/**
 * First touch shaped for the GraphQL `attribution` argument.
 *
 * Two things this exists to enforce, both of which have already gone wrong:
 *
 * 1. It must be passed NESTED (`attribution: firstTouchAttributionArg()`), not
 *    spread flat into the variables. `captureLead`/`completeOnboarding` declare
 *    `$attribution: AttributionInput`, so top-level `gclid`/`utmSource` keys are
 *    undeclared variables the server never sees — the mutation still succeeds
 *    and `Church.attribution` lands NULL. That is exactly what the v2 rewrite
 *    did, and it silently dropped every signup's source.
 * 2. `ref` is stripped. The schema's `AttributionInput` does not declare it (the
 *    server injects the resolved code itself via `withReferralCode`), and
 *    GraphQL rejects the WHOLE mutation on an undeclared input field — so a
 *    stray `ref` would fail signup closed, not merely lose attribution. The
 *    browser-side `FirstTouch` type carries `ref`, and `seedAttribution` is
 *    typed with it, so drop it here once rather than at each call site.
 */
export function firstTouchAttributionArg(): Omit<FirstTouch, 'ref'> {
  const touch: FirstTouch = { ...getFirstTouch() };

  delete touch.ref;

  return touch;
}

/**
 * Seed first-touch attribution with page-supplied defaults when nothing has
 * been recorded yet. Real URL signals always win over the defaults, so a
 * genuinely UTM-tagged landing is never overwritten. Use this on landing pages
 * whose inbound link we don't fully control — e.g. the Planning Center
 * integration directory links to a bare /start/pco with no utm_*, but we still
 * want those leads attributed to planningcenter. First touch still wins: if the
 * cookie already exists it is left untouched.
 */
export function seedFirstTouch(defaults: FirstTouch): void {
  if (typeof window === 'undefined') return;

  const cookies = new Cookies();

  if (cookies.get(COOKIE)) return; // first touch already recorded

  const fromUrl = readFromUrl();
  const merged: FirstTouch = { ...defaults };

  // Real signals from the URL take precedence over the supplied defaults.
  // (Object.keys cast is safe: readFromUrl builds fromUrl from FirstTouch
  // keys only.)
  for (const key of Object.keys(fromUrl) as (keyof FirstTouch)[]) {
    const value = fromUrl[key];

    if (value) merged[key] = value;
  }

  cookies.set(COOKIE, merged, {
    maxAge: MAX_AGE_SECONDS,
    path: '/',
    sameSite: 'lax',
    secure: secureCookie(),
  });
}

/**
 * Records first-touch attribution exactly once per browser, from the current
 * URL + referrer. First touch wins: once the cookie exists it is never
 * overwritten, so later same-session navigation (which loses the original
 * utm_* query) can't clobber the real origin. Safe to call from any surface —
 * legacy pages call it per-route via useFirstTouchAttribution; App Router
 * signup surfaces without a marketing layout (e.g. /sign-up, which
 * hi.onark.app links into with forwarded UTMs) call it on mount.
 */
export function recordFirstTouch(): void {
  if (typeof window === 'undefined') return;

  const cookies = new Cookies();

  if (cookies.get(COOKIE)) return; // first touch already recorded

  const touch = readFromUrl();
  // Don't store an empty record (no utm, no click id, no off-site referrer):
  // that would lock in a meaningless "direct" first touch and block a later,
  // genuinely-attributed landing in the same session from being recorded.
  const hasSignal =
    touch.utmSource ||
    touch.utmMedium ||
    touch.utmCampaign ||
    touch.gclid ||
    touch.fbclid ||
    touch.referrer ||
    touch.adId;

  if (!hasSignal) return;

  cookies.set(COOKIE, touch, {
    maxAge: MAX_AGE_SECONDS,
    path: '/',
    sameSite: 'lax',
    secure: secureCookie(),
  });
}

/**
 * Legacy pages-router hook: record first touch on every route so the very
 * first landing — which may be a marketing page, not the signup flow — is the
 * one that sticks.
 */
export function useFirstTouchAttribution(): void {
  const router = useRouter();

  useEffect(() => {
    recordFirstTouch();
  }, [router.asPath]);
}
